<template>
	<v-card :class="embedded ? '' : 'ma-5'" flat>
		<v-card-text :class="embedded ? 'pa-3' : ''">
			<!-- Loading collections -->
			<div v-if="loadingCollections" class="d-flex justify-center align-center py-8">
				<v-progress-circular indeterminate color="primary" />
			</div>

			<!-- Error loading collections -->
			<v-alert
				v-else-if="collectionsError"
				type="error"
				outlined
				dense
				class="mb-0"
			>
				{{ collectionsError }}
			</v-alert>

			<!-- Main content -->
			<div v-else class="pitr-content">
				<!-- Header info -->
				<v-alert
					type="warning"
					outlined
					dense
					class="mb-4"
					icon="mdi-history"
				>
					<span class="font-weight-bold">{{ $t('pitr-title') }}</span>
					<span class="ml-1">— {{ $t('pitr-description') }}</span>
				</v-alert>

				<!-- Form -->
				<v-form ref="pitrForm" v-model="formValid">
					<!-- Collection selector -->
					<v-autocomplete
						v-model="form.collection"
						:items="collections"
						:label="$t('pitr-collection')"
						:rules="[rules.required]"
						outlined
						dense
						prepend-inner-icon="mdi-database"
						class="mb-1"
						:placeholder="$t('pitr-select-collection')"
					/>

					<!-- Document ID -->
					<v-text-field
						v-model="form.documentId"
						:label="$t('pitr-document-id')"
						:rules="[rules.required]"
						outlined
						dense
						prepend-inner-icon="mdi-identifier"
						class="mb-1"
						:placeholder="$t('pitr-enter-document-id')"
					/>

					<!-- Date & Time picker (EST) -->
					<div class="d-flex align-start">
						<v-text-field
							v-model="form.date"
							:label="$t('pitr-date-est')"
							type="date"
							:max="todayEST"
							:min="minDateEST"
							:rules="[rules.required]"
							outlined
							dense
							prepend-inner-icon="mdi-calendar"
							class="mr-2"
							style="flex: 1;"
						/>
						<v-text-field
							v-model="form.time"
							:label="$t('pitr-time-est')"
							type="time"
							:rules="[rules.required]"
							outlined
							dense
							prepend-inner-icon="mdi-clock-outline"
							style="flex: 1;"
						/>
					</div>

					<!-- Timestamp validation error -->
					<v-alert
						v-if="timestampError"
						type="error"
						outlined
						dense
						class="mb-3"
						icon="mdi-alert-circle"
					>
						{{ timestampError }}
					</v-alert>

					<!-- Action buttons -->
					<div class="d-flex align-center mb-4">
						<v-btn
							color="primary"
							:loading="loadingPreview"
							:disabled="!canPreview"
							class="mr-3"
							@click="preview"
						>
							<v-icon left small>mdi-eye</v-icon>
							{{ $t('preview') }}
						</v-btn>
						<v-btn
							color="deep-orange"
							dark
							:loading="loadingWrite"
							:disabled="!canWrite"
							@click="confirmWrite"
						>
							<v-icon left small>mdi-restore</v-icon>
							{{ $t('pitr-write-restore') }}
						</v-btn>
						<v-spacer />
						<v-btn
							v-if="previewData"
							text
							small
							color="grey"
							@click="clearPreview"
						>
							<v-icon left small>mdi-close</v-icon>
							{{ $t('clear') }}
						</v-btn>
					</div>
				</v-form>

				<!-- Preview result -->
				<div v-if="previewData" class="preview-section">
					<div class="preview-header d-flex align-center mb-2">
						<v-icon small color="primary" class="mr-2">mdi-file-document-outline</v-icon>
						<span class="font-weight-bold text-subtitle-2">{{ $t('pitr-document-preview') }}</span>
						<v-spacer />
						<v-chip x-small color="primary" outlined>
							{{ form.collection }} / {{ form.documentId }}
						</v-chip>
						<v-btn
							icon
							x-small
							class="ml-2"
							@click="copyPreviewData"
						>
							<v-icon x-small>mdi-content-copy</v-icon>
						</v-btn>
					</div>
					<div class="preview-body">
						<pre class="preview-json">{{ formattedPreviewData }}</pre>
					</div>
				</div>

				<!-- Preview error -->
				<v-alert
					v-if="previewError"
					type="error"
					outlined
					dense
					class="mt-2"
					dismissible
				>
					{{ previewError }}
				</v-alert>

				<!-- Write result -->
				<v-alert
					v-if="writeSuccess"
					ref="writeSuccessAlert"
					type="success"
					outlined
					dense
					class="mt-2"
					dismissible
				>
					{{ $t('pitr-document-restored') }}
				</v-alert>

				<v-alert
					v-if="writeError"
					ref="writeErrorAlert"
					type="error"
					outlined
					dense
					class="mt-2"
					dismissible
				>
					{{ writeError }}
				</v-alert>
			</div>
		</v-card-text>

		<!-- Write confirmation dialog -->
		<v-dialog v-model="confirmDialog" max-width="450" persistent content-class="devops-subdialog">
			<v-card class="py-4 px-2">
				<v-card-title class="text-h6 font-weight-bold pb-2">
					<v-icon color="deep-orange" class="mr-2">mdi-alert</v-icon>
					{{ $t('pitr-confirm-restore') }}
					<v-spacer />
					<v-btn icon small @click="confirmDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider class="mb-3" />
				<v-card-text>
					<p class="mb-2">
						{{ $t('pitr-overwrite-warning') }}
					</p>
					<div class="confirm-details pa-3 mb-3">
						<div><strong>{{ $t('pitr-collection') }}:</strong> {{ form.collection }}</div>
						<div><strong>{{ $t('pitr-document-id') }}:</strong> {{ form.documentId }}</div>
						<div><strong>{{ $t('pitr-timestamp-est') }}:</strong> {{ form.date }} {{ form.time }}</div>
					</div>
					<p class="red--text text--darken-2 font-weight-medium mb-0">
						{{ $t('pitr-action-cannot-be-undone') }}
					</p>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="confirmDialog = false">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn
						color="deep-orange"
						dark
						:loading="loadingWrite"
						@click="write"
					>
						<v-icon left small>mdi-restore</v-icon>
						{{ $t('pitr-confirm-restore') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
export default {
	name: "PitrPanel",

	props: {
		embedded: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			collections: [],
			loadingCollections: false,
			collectionsError: null,
			form: {
				collection: null,
				documentId: "",
				date: "",
				time: ""
			},
			formValid: false,
			rules: {
				required: v => !!v || this.$t("pitr-field-required")
			},
			previewData: null,
			previewError: null,
			loadingPreview: false,
			writeSuccess: false,
			writeError: null,
			loadingWrite: false,
			confirmDialog: false
		};
	},

	computed: {
		/**
		 * Today's date string in EST (YYYY-MM-DD)
		 */
		todayEST() {
			return this.formatDateEST(new Date());
		},

		/**
		 * Minimum allowed date (7 days ago) in EST (YYYY-MM-DD)
		 */
		minDateEST() {
			const d = new Date();

			d.setTime(d.getTime() - 7 * 24 * 60 * 60 * 1000);
			return this.formatDateEST(d);
		},

		/**
		 * Convert the selected EST date/time to a UTC epoch in seconds.
		 * Returns null if inputs are incomplete.
		 */
		utcTimestampSeconds() {
			if (!this.form.date || !this.form.time) return null;

			// Build an ISO string interpreting the input as EST (America/New_York)
			// We use Intl to resolve the EST/EDT offset for the chosen date
			const estDateTimeStr = `${this.form.date}T${this.form.time}`;
			const utcMs = this.estToUtcMs(estDateTimeStr);

			return Math.floor(utcMs / 1000);
		},

		/**
		 * Validate that the selected timestamp is within the 7-day window
		 */
		timestampError() {
			if (this.utcTimestampSeconds === null) return null;

			const now = Math.floor(Date.now() / 1000);
			const maxAge = 7 * 24 * 60 * 60;

			if (this.utcTimestampSeconds > now) {
				return this.$t("pitr-timestamp-future");
			}
			if (now - this.utcTimestampSeconds > maxAge) {
				return this.$t("pitr-timestamp-too-old");
			}
			return null;
		},

		canPreview() {
			return (
				this.formValid &&
				!this.timestampError &&
				this.utcTimestampSeconds !== null &&
				!this.loadingPreview &&
				!this.loadingWrite
			);
		},

		canWrite() {
			return (
				this.previewData &&
				!this.timestampError &&
				!this.loadingPreview &&
				!this.loadingWrite
			);
		},

		formattedPreviewData() {
			if (!this.previewData) return "";
			return JSON.stringify(this.previewData, null, 2);
		}
	},

	created() {
		this.fetchCollections();
		this.setDefaultDateTime();
	},

	methods: {
		/**
		 * Format a JS Date to YYYY-MM-DD in America/New_York timezone
		 */
		formatDateEST(date) {
			const parts = new Intl.DateTimeFormat("en-CA", {
				timeZone: "America/New_York",
				year: "numeric",
				month: "2-digit",
				day: "2-digit"
			}).formatToParts(date);

			const y = parts.find(p => p.type === "year").value;
			const m = parts.find(p => p.type === "month").value;
			const d = parts.find(p => p.type === "day").value;

			return `${y}-${m}-${d}`;
		},

		/**
		 * Convert a datetime string like "2025-02-19T14:30:00" interpreted as EST
		 * into UTC milliseconds, correctly handling EST/EDT.
		 */
		estToUtcMs(dateTimeStr) {
			// Create a temporary date to figure out the UTC offset for America/New_York
			// on the selected date. We parse the date parts manually.
			const [datePart, timePart] = dateTimeStr.split("T");
			const [year, month, day] = datePart.split("-").map(Number);
			const timeParts = timePart.split(":").map(Number);
			const hours = timeParts[0] || 0;
			const minutes = timeParts[1] || 0;
			const seconds = timeParts[2] || 0;

			// Use a formatter to get the offset for the given date in America/New_York
			// We create a rough UTC date first, then find the actual offset
			const roughUtc = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));

			// Get the EST representation of this rough UTC date to compute offset
			const estFormatter = new Intl.DateTimeFormat("en-US", {
				timeZone: "America/New_York",
				year: "numeric",
				month: "numeric",
				day: "numeric",
				hour: "numeric",
				minute: "numeric",
				second: "numeric",
				hour12: false
			});

			const estParts = estFormatter.formatToParts(roughUtc);
			const estHour = parseInt(estParts.find(p => p.type === "hour").value);
			const estDay = parseInt(estParts.find(p => p.type === "day").value);

			// Compute the offset: EST time = UTC time + offset
			// So offset = EST hour - UTC hour (simplified; handles day boundary)
			let offsetHours = estHour - roughUtc.getUTCHours();

			if (estDay < roughUtc.getUTCDate()) offsetHours -= 24;
			if (estDay > roughUtc.getUTCDate()) offsetHours += 24;

			// offsetHours is typically -5 (EST) or -4 (EDT)
			// To get UTC from EST: UTC = EST - offset
			const utcMs = Date.UTC(year, month - 1, day, hours - offsetHours, minutes, seconds);

			return utcMs;
		},

		setDefaultDateTime() {
			// Default to 1 hour ago in EST
			const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

			this.form.date = this.formatDateEST(oneHourAgo);

			const timeParts = new Intl.DateTimeFormat("en-US", {
				timeZone: "America/New_York",
				hour: "2-digit",
				minute: "2-digit",
				hour12: false
			}).formatToParts(oneHourAgo);

			const h = timeParts.find(p => p.type === "hour").value;
			const m = timeParts.find(p => p.type === "minute").value;

			this.form.time = `${h}:${m}`;
		},

		async fetchCollections() {
			this.loadingCollections = true;
			this.collectionsError = null;

			try {
				const { data } = await this.axios.get("/devops/collections");

				this.collections = data;
			} catch (err) {
				console.error("Failed to fetch collections:", err);
				this.collectionsError = this.$t("pitr-failed-load-collections");
			} finally {
				this.loadingCollections = false;
			}
		},

		async preview() {
			if (!this.canPreview) return;

			this.loadingPreview = true;
			this.previewError = null;
			this.previewData = null;
			this.writeSuccess = false;
			this.writeError = null;

			try {
				const { data } = await this.axios.post("/devops/pitr/preview", {
					collection: this.form.collection,
					documentId: this.form.documentId.trim(),
					timestampSeconds: this.utcTimestampSeconds
				});

				this.previewData = data;
			} catch (err) {
				console.error("PITR preview failed:", err);
				this.previewError =
					err?.response?.data?.message ||
					this.$t("pitr-failed-preview");
			} finally {
				this.loadingPreview = false;
			}
		},

		confirmWrite() {
			this.confirmDialog = true;
		},

		async write() {
			this.loadingWrite = true;
			this.writeError = null;
			this.writeSuccess = false;

			try {
				await this.axios.post("/devops/pitr/write", {
					collection: this.form.collection,
					documentId: this.form.documentId.trim(),
					timestampSeconds: this.utcTimestampSeconds
				});

				this.writeSuccess = true;
				this.confirmDialog = false;
				this.$nextTick(() => {
					this.scrollToResult("writeSuccessAlert");
				});
			} catch (err) {
				console.error("PITR write failed:", err);
				this.writeError =
					err?.response?.data?.message ||
					this.$t("pitr-failed-restore");
				this.$nextTick(() => {
					this.scrollToResult("writeErrorAlert");
				});
			} finally {
				this.loadingWrite = false;
			}
		},

		clearPreview() {
			this.previewData = null;
			this.previewError = null;
			this.writeSuccess = false;
			this.writeError = null;
		},

		scrollToResult(refName) {
			const el = this.$refs[refName]?.$el || this.$refs[refName];

			if (!el) return;

			// Find the scrollable parent (.panel-dialog-content)
			let scrollParent = el.parentElement;

			while (scrollParent) {
				const overflow = getComputedStyle(scrollParent).overflowY;

				if (overflow === "auto" || overflow === "scroll") break;
				scrollParent = scrollParent.parentElement;
			}

			if (scrollParent) {
				scrollParent.scrollTo({
					top: scrollParent.scrollHeight,
					behavior: "smooth"
				});
			} else {
				el.scrollIntoView({ behavior: "smooth", block: "end" });
			}
		},

		async copyPreviewData() {
			if (!this.previewData) return;
			try {
				await navigator.clipboard.writeText(this.formattedPreviewData);
				this.$success(this.$t("copied-to-clipboard"));
			} catch (err) {
				console.error("Failed to copy:", err);
			}
		}
	}
};
</script>

<style scoped>
	.pitr-content {
		max-width: 100%;
	}

	@media (max-width: 599px) {
		.pitr-content {
			overflow-y: auto;
		}
	}

	.preview-section {
		border: 1px solid rgba(0, 0, 0, 0.12);
		border-radius: 8px;
		overflow: hidden;
	}

	.preview-header {
		background: rgba(25, 118, 210, 0.05);
		padding: 8px 12px;
		border-bottom: 1px solid rgba(0, 0, 0, 0.08);
	}

	.preview-body {
		max-height: 400px;
		overflow: auto;
		background: #fafafa;
	}

	.preview-json {
		margin: 0;
		padding: 12px 16px;
		font-size: 12px;
		line-height: 1.5;
		font-family: "Fira Code", "Consolas", "Monaco", monospace;
		white-space: pre-wrap;
		word-break: break-word;
		color: #333;
	}

	.confirm-details {
		background: rgba(0, 0, 0, 0.04);
		border-radius: 6px;
		font-size: 13px;
		line-height: 1.8;
	}

	/* Dark mode support */
	.theme--dark .preview-body {
		background: #1e1e1e;
	}

	.theme--dark .preview-json {
		color: #d4d4d4;
	}

	.theme--dark .preview-header {
		background: rgba(25, 118, 210, 0.15);
	}

	.theme--dark .confirm-details {
		background: rgba(255, 255, 255, 0.06);
	}
</style>
